import { Injectable } from "@angular/core";
import { AuthorizationCodeGrantService } from "./grants/authorization-code-grant/authorization-code-grant.service";
import { AuthorizationHeaderFactoryService } from "../http/headers/authorization-header-factory.service";
import { RefreshTokenGrantService } from "./grants/refresh-token-grant/refresh-token-grant.service";
import { LogoutService } from "./logout.service";
import { AuthStorageService } from "./auth-storage.service";
import { AuthModule } from './auth.module';
import { PKCEGrantService } from './grants/authorization-code-grant/p-k-c-e/p-k-c-e-grant.service';

@Injectable({
  providedIn: AuthModule
})
export class AuthService {

  constructor(
    private pkceCodeGrantService: PKCEGrantService,
    private authorizationCodeGrantService: AuthorizationCodeGrantService,
    private authHeaderFactoryService: AuthorizationHeaderFactoryService,
    private refreshTokenService: RefreshTokenGrantService,
    private logoutService: LogoutService,
    private authStorageService: AuthStorageService
  ) {}

  createRefreshTokenRequestUrl() {
    return this.refreshTokenService.createRefreshTokenRequestUrl();
  }

  generateVerifier() {
    return this.pkceCodeGrantService.generateVerifier();
  }

  storeVerifier(verifier) {
    return this.pkceCodeGrantService.storeVerifier(verifier);
  }

  createAuthorizationRequestUrl() {
    return this.authorizationCodeGrantService.createAuthorizationRequestUrl();
  }

  async createCodeChallenge(verifier) {
    return await this.pkceCodeGrantService.createCodeChallenge(verifier);
  }

  createPkceAuthorizationRequestUrl(codeChallenge, prompt?) {
    return this.pkceCodeGrantService.createAuthorizationRequestUrl(
      codeChallenge,
      prompt
    );
  }

  loadVerifier() {
    return this.pkceCodeGrantService.loadVerifier();
  }

  runPkceAuthorizationCodeExchange(code, verifier) {
    return this.pkceCodeGrantService.runAuthorizationCodeExchange(
      code,
      verifier
    );
  }

  createPkceAuthorizationCodeRequestUrl() {
    return this.pkceCodeGrantService.createPkceAuthorizationCodeRequestUrl();
  }

  runAuthorizationCodeExchange(code) {
    return this.authorizationCodeGrantService.runAuthorizationCodeExchange(
      code
    );
  }

  storeRefreshToken(refreshToken) {
    return this.authStorageService.storeRefreshToken(refreshToken);
  }

  storeToken(token) {
    return this.authStorageService.storeToken(token);
  }

  clearData() {
    return this.authStorageService.clearAll();
  }

  clearToken() {
    this.authStorageService.clearToken();
  }

  clearRefreshToken() {
    this.authStorageService.clearRefreshToken();
  }

  clearVerifier() {
    this.authStorageService.clearVerifier();
  }

  clearTokenCounterUsage() {
    this.authStorageService.clearTokenCounterUsage();
  }

  loadToken() {
    return this.authStorageService.loadToken();
  }

  createAuthHeader() {
    return this.authHeaderFactoryService.createHeader();
  }

  createRefreshTokenRequestBody() {
    return this.refreshTokenService.createBody();
  }

  createRefreshTokenOptions() {
    return this.refreshTokenService.createOptions();
  }

  postRefreshTokenRequest() {
    return this.refreshTokenService.postRequest();
  }

  loadRefreshToken() {
    return this.refreshTokenService.loadRefreshToken();
  }

  logout() {
    this.logoutService.logout();
  }

  storeCodeChallenge(codeChallenge) {
    return this.pkceCodeGrantService.storeCodeChallenge(codeChallenge);
  }

  loadCodeChallenge() {
    return this.pkceCodeGrantService.loadCodeChallenge();
  }

  storeExpiresIn(expires_in) {
    return this.authStorageService.storeExpiresIn(expires_in);
  }

  loadExpiresIn() {
    return this.authStorageService.loadExpiresIn();
  }

  clearExpiresIn() {
    return this.authStorageService.clearExpiresIn();
  }

  storeTokenStoreTime(time) {
    return this.authStorageService.storeTokenStoreTime(time);
  }

  loadTokenStoreTime() {
    return this.authStorageService.loadTokenStoreTime();
  }

  clearTokenStoreTime() {
    return this.authStorageService.clearTokenStoreTime();
  }
}
